import type { MovingRect } from './canvasAlignment'
import { getShovePlan } from './canvasShove'
import type { ShoveAxis, ShoveTarget } from './canvasShove'
import { freeSlotsFor } from './canvasSlots'
import { viewOf } from './canvasSpawn'
import { swapPositions } from './canvasSwap'
import type { SwapEntry, SwapPoint } from './canvasSwap'

/**
 * Die **Ableg-Flächen** am **Tauschpunkt** (CONTEXT.md): Jeder Zettel zerfällt
 * beim Ziehen in **Kern** und **Rand** — der Kern tauscht, der Rand reiht ein.
 * Beide meinen denselben Platz; sie unterscheiden sich nur darin, was mit dem
 * Zettel geschieht, auf den man zeigt. Daneben, im Freien, liegen die
 * **Leerplätze** (`canvasSlots.ts`): Felder, in die man hinlegt, ohne dass
 * jemand rückt. Die Trennlinie ist scharf — Rand heißt, es rückt jemand;
 * Leerplatz heißt, es rückt niemand.
 *
 * **Warum auf dem Zettel und nicht in der Lücke** (Entwurf 2026-09-04): Die
 * Lücke ist einen **Takt** breit, herausgezoomt also eine Handvoll
 * Bildschirm-Pixel, und sie liegt HINTER der Tauschfläche — man musste durch
 * den Tausch hindurch zielen. Auf dem Zettel liegt der Rand VOR dem Kern: Wer
 * von außen kommt, kreuzt erst den Rand. Damit braucht der Tausch keine
 * Wartezeit mehr, und die Richtungsmessung ist entfallen.
 *
 * Reines Rechenwerk; die Geste liegt in `useSwapDrag`, das Bild entsteht durch
 * das Ausweichen der Zettel selbst (`getSpreadPlan`).
 */

/** Wie weit der Rand nach innen greift — Bildschirm-px, wie jede Trefferschwelle hier. */
export const EDGE_WIDTH = 28

/**
 * Der Deckel: Ein Rand nimmt höchstens ein Viertel seiner Kante ein, je Achse.
 * Ohne ihn fräße er bei starkem Herauszoomen oder auf kleinen Zetteln den Kern
 * auf — 28 Bildschirm-px sind bei Viertel-Zoom 112 Canvas-px. So bleibt dem
 * Kern immer mindestens die Hälfte.
 */
export const EDGE_MAX_SHARE = 0.25

export interface SwapZone {
  /** Wem die Fläche gehört. */
  owner: SwapEntry
  /**
   * `on` ist der Kern (tauschen), `before`/`after` sind die Ränder
   * (einreihen), `slot` ein Leerplatz (hinlegen, niemand rückt) — dessen
   * `owner` ist der Zettel, an dessen Seite das Feld hängt.
   *
   * `home` ist der Platz, den der Getragene gerade verlässt: ein gültiges
   * Ziel wie jedes andere — er trägt seine **Zielfläche** —, aber ein
   * folgenloses. Loslassen schreibt dort nichts, niemand rückt, der Ursprung
   * schließt sich nicht; er ist ja das Ziel. **Hier entsteht er nie**
   * (`swapZonesFor` kennt ihn nicht): Als **Leerplatz** wäre er auch über den
   * `nearest`-Rückfall und über die gewachsene Trefferfläche fremder Felder
   * erreichbar, und `getCloseOriginPlan` rechnete ihm eine schließende Reihe
   * an, die es nicht gibt. Er gehört der Geste (`useSwapDrag`).
   */
  kind: 'before' | 'on' | 'after' | 'slot' | 'home'
  /** Nur bei den Rändern: die Reihenachse. */
  axis?: ShoveAxis
  /** Canvas-Koordinaten. */
  rect: MovingRect
}

export interface SwapZones {
  /** Die Ränder des getroffenen Zettels, oder der getroffene Leerplatz — der Kern steht hier nie. */
  zones: SwapZone[]
  hit: SwapZone | null
}

export interface SwapZonesOptions {
  /** Ohne Ränder und Leerplätze (nur Kern) — aus der Gruppe heraus wird nur getauscht. */
  bands?: boolean
  /**
   * Die Lage des GETRAGENEN — dann gibt es keinen Fehlschlag: Trifft der
   * Zeiger nichts, gilt das **Leerplatz**, das dieser Lage am nächsten liegt.
   *
   * **Im Ring wird angedockt, draußen wird verschoben — dazwischen gibt es
   * nichts** (CONTEXT.md, Tauschpunkt). Zwischen den Feldern und in ihren
   * Ecken bleiben Lücken; wer dort losließ, fiel in den freien Zustand und
   * rastete aufs Punktraster, mitten in der Gruppe (gemeldet 2026-09-05: „das
   * Raster der Gruppe trifft er nie"). Wer die Angabe weglässt, bekommt den
   * Fehlschlag wie bisher — die Geste entscheidet, wann sie draußen ist
   * (`useSwapDrag`).
   */
  nearest?: MovingRect
}

const lengthOf = (rect: MovingRect, axis: ShoveAxis) => (axis === 'x' ? rect.width : rect.height)

/**
 * Der dichteste Nachbar auf einer Achse und Seite — quer überlappend, Lücke
 * höchstens eine Zettellänge (die Reihen-Regel aus `rowAfter`).
 */
function neighbourOn(entry: SwapEntry, others: readonly SwapEntry[], axis: ShoveAxis, side: 'before' | 'after'): SwapEntry | null {
  const horizontal = axis === 'x'
  const me = viewOf(entry.rect, horizontal)
  const reach = lengthOf(entry.rect, axis)
  let best: { entry: SwapEntry; gap: number } | null = null
  for (const other of others) {
    const v = viewOf(other.rect, horizontal)
    if (!(v.crossEnd > me.crossStart && v.crossStart < me.crossEnd)) continue
    const gap = side === 'after' ? v.lead - me.trail : me.lead - v.trail
    if (gap < 0 || gap > reach) continue
    if (!best || gap < best.gap) best = { entry: other, gap }
  }
  return best
    ? best.entry
    : null
}

/** Der Abstand zum dichtesten Nachbarn auf dieser Seite — `null`, wenn dort keiner steht. */
function gapOn(entry: SwapEntry, others: readonly SwapEntry[], axis: ShoveAxis, side: 'before' | 'after'): number | null {
  const neighbour = neighbourOn(entry, others, axis, side)
  if (!neighbour) return null
  const horizontal = axis === 'x'
  const me = viewOf(entry.rect, horizontal)
  const v = viewOf(neighbour.rect, horizontal)
  return side === 'after' ? v.lead - me.trail : me.lead - v.trail
}

/** Ein Streifen auf der Achse: von `from` bis `to`, quer so breit wie der Besitzer. */
function band(owner: SwapEntry, axis: ShoveAxis, from: number, to: number): MovingRect {
  return axis === 'x'
    ? { x: from, y: owner.rect.y, width: to - from, height: owner.rect.height }
    : { x: owner.rect.x, y: from, width: owner.rect.width, height: to - from }
}

const samePlace = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5

/**
 * Ob Loslassen auf diesem Rand NICHTS änderte: Der Getragene landete, wo er
 * herkam, und niemand rückte. Das sind die Kanten unmittelbar an seinem
 * Ursprung — die folgende seines Vorgängers, die führende seines Nachfolgers.
 * Solche Ränder gibt es nicht; dort gilt der Kern (die zweite Skizze). Ein
 * Angebot, das nichts tut, ist keines. Gerechnet, nicht geraten: derselbe
 * Plan wie beim Loslassen.
 */
function idle(entries: readonly SwapEntry[], dragged: SwapEntry, owner: SwapEntry, axis: ShoveAxis, side: 'before' | 'after'): boolean {
  const plan = getShovePlan([...entries], dragged.id, { axis, anchor: owner, side }, true)
  if (!samePlace(plan.rect, dragged.rect)) return false
  return plan.moves.every(move => {
    const entry = entries.find(e => e.id === move.id)
    return entry !== undefined && samePlace(move, entry.rect)
  })
}

/**
 * Ob dieser Rand DASSELBE täte wie ein **Platz tauschen** — dann ist er keiner.
 *
 * Der Fall: In einer dichten Reihe einen Nachbarn von seiner fernen Kante her
 * einreihen. Der Getragene nimmt dessen Platz, der Nachbar rückt eins weiter,
 * und der Ursprung schließt — am Ende stehen beide genau so, wie ein Tausch
 * sie hingestellt hätte. Zwei Bilder für einen Ausgang: Beim Hochziehen sähe
 * man erst den Partner springen und gleich darauf die ganze Reihe ausweichen,
 * obwohl dasselbe herauskommt (gemeldet 2026-09-04). Es bleibt der Kern, und
 * der reicht dann bis an die Kante.
 *
 * **Verglichen wird mit dem Tausch, den dieser Ausgang bedeutet**, nicht mit
 * dem Tausch am Eigentümer des Randes: Bei drei Zetteln nebeneinander tut die
 * führende Kante des DRITTEN dasselbe wie ein Tausch mit dem ZWEITEN — der
 * Erste rückt dorthin, der Zweite auf dessen Platz, der Dritte bleibt stehen.
 * Gesucht wird darum, wer auf dem Platz saß, den der Getragene einnimmt.
 *
 * Gerechnet, nicht geraten — wie bei `idle`: Bei ungleich großen Zetteln oder
 * einem Abstand, der nicht dem **Takt** entspricht, fallen beide Ausgänge
 * auseinander, und dann ist der Rand ein echtes zweites Angebot.
 */
function sameAsSwap(entries: readonly SwapEntry[], dragged: SwapEntry, owner: SwapEntry, axis: ShoveAxis, side: 'before' | 'after'): boolean {
  const plan = getShovePlan([...entries], dragged.id, { axis, anchor: owner, side }, true)
  // Wer stand da, wo der Getragene landet? Nur mit dem wäre es ein Tausch.
  const partner = entries.find(entry => entry.id !== dragged.id && samePlace(entry.rect, plan.rect))
  if (!partner) return false
  const swapped = swapPositions(dragged.rect, partner.rect)
  if (!samePlace(plan.rect, swapped.a)) return false
  return plan.moves.every(move => {
    if (move.id === partner.id) return samePlace(move, swapped.b)
    // Alle übrigen stehen beim Tausch still — wer rückt, weicht ab.
    const entry = entries.find(e => e.id === move.id)
    return entry !== undefined && samePlace(move, entry.rect)
  })
}

/**
 * Ob Loslassen auf diesem Rand jemanden RÜCKT. Ein Rand, der niemanden rückt
 * — hinten am Letzten einer Reihe, wo er nur anhängte —, ist keiner: Das
 * Anhängen ist Sache des Leerplatzes dahinter, der in der Flucht des Rasters
 * liegt. So gilt ohne Ausnahme: Rand heißt, es rückt jemand.
 */
function pushes(entries: readonly SwapEntry[], dragged: SwapEntry, owner: SwapEntry, axis: ShoveAxis, side: 'before' | 'after'): boolean {
  return getShovePlan([...entries], dragged.id, { axis, anchor: owner, side }, false).moves.length > 0
}

/**
 * Kern und Ränder EINES Zettels.
 *
 * Ränder gibt es nur auf Achsen, auf denen der Zettel in einer **Reihe** steht
 * — sonst gäbe es kein Dazwischen, sondern nur einen Nachbarn, den man
 * wegschöbe. Auf einer solchen Achse ist jede Kante Rand, hinter der jemand
 * rückt (`pushes`) — es sei denn, Loslassen änderte dort nichts (`idle`) oder
 * täte genau das, was der Kern tut (`sameAsSwap`). Der Kern ist, was nach den
 * Rändern übrig bleibt; wo einer wegfällt, reicht er bis an die Kante.
 *
 * Die Reihe wird MIT dem Getragenen bestimmt: Ob ein Zettel in einer Reihe
 * steht, ist eine Eigenschaft der Lage, und die ändert der Griff nicht.
 */
function zonesOf(owner: SwapEntry, entries: readonly SwapEntry[], dragged: SwapEntry, zoom: number, bands: boolean): SwapZone[] {
  const edges: SwapZone[] = []
  const inset: Partial<Record<'x' | 'y', { lead: number; trail: number }>> = {}
  if (bands) {
    const others = entries.filter(entry => entry.id !== owner.id)
    const reach = EDGE_WIDTH / zoom
    for (const axis of ['x', 'y'] as const) {
      const before = gapOn(owner, others, axis, 'before')
      const after = gapOn(owner, others, axis, 'after')
      // Ohne Nachbarn auf dieser Achse steht der Zettel hier in keiner Reihe.
      if (before === null && after === null) continue
      const me = viewOf(owner.rect, axis === 'x')
      const depth = Math.min(reach, lengthOf(owner.rect, axis) * EDGE_MAX_SHARE)
      // Nach außen bis in die halbe Lücke — dann berühren sich die Ränder
      // zweier Nachbarn nie, und die Lücke ist trotzdem kein totes Land.
      const outward = (gap: number | null) => (gap === null ? reach : Math.min(reach, gap / 2))
      const sides = [
        { kind: 'before' as const, from: me.lead - outward(before), to: me.lead + depth },
        { kind: 'after' as const, from: me.trail - depth, to: me.trail + outward(after) },
      ]
      const cut = { lead: 0, trail: 0 }
      for (const side of sides) {
        if (idle(entries, dragged, owner, axis, side.kind)) continue
        if (!pushes(entries, dragged, owner, axis, side.kind)) continue
        if (sameAsSwap(entries, dragged, owner, axis, side.kind)) continue
        edges.push({ owner, kind: side.kind, axis, rect: band(owner, axis, side.from, side.to) })
        if (side.kind === 'before') cut.lead = depth
        else cut.trail = depth
      }
      inset[axis] = cut
    }
  }
  // Der Kern: der Zettel, um die Ränder beschnitten, die es wirklich gibt.
  const x = inset.x ?? { lead: 0, trail: 0 }
  const y = inset.y ?? { lead: 0, trail: 0 }
  const core: MovingRect = {
    x: owner.rect.x + x.lead,
    y: owner.rect.y + y.lead,
    width: owner.rect.width - x.lead - x.trail,
    height: owner.rect.height - y.lead - y.trail,
  }
  return [...edges, { owner, kind: 'on', rect: core }]
}

const contains = (rect: MovingRect, p: SwapPoint) =>
  p.x >= rect.x && p.x <= rect.x + rect.width && p.y >= rect.y && p.y <= rect.y + rect.height

/** Der Abstand des Zeigers zur Kante, die dieser Rand meint. */
const distanceToEdge = (zone: SwapZone, p: SwapPoint) => {
  const horizontal = zone.axis === 'x'
  const me = viewOf(zone.owner.rect, horizontal)
  const along = horizontal ? p.x : p.y
  return Math.abs(along - (zone.kind === 'before' ? me.lead : me.trail))
}

/** Der Abstand des Zeigers zur Mitte eines Feldes. */
const distanceToCentre = (rect: MovingRect, p: SwapPoint) =>
  Math.hypot(p.x - (rect.x + rect.width / 2), p.y - (rect.y + rect.height / 2))

/**
 * Die Trefferfläche eines Feldes: das Feld selbst, um **eine Handbreit** auf
 * seinen Zettel zu gewachsen (`EDGE_WIDTH`, höchstens bis an dessen Kante).
 *
 * Warum überhaupt: Das Feld liegt eine Zettellänge plus **Takt** vom Zettel
 * entfernt, dazwischen blieb ein toter Streifen so breit wie der Takt. Wer
 * „ein Stück über den Zettel" zielte, traf ins Leere — deshalb ließ sich ein
 * Platz oberhalb einer Reihe kaum finden. Wo ein **Rand** in den Streifen
 * ragt, geht er vor: Er wird zuerst geprüft.
 *
 * Warum nur eine Handbreit: Der Zettel landet im FELD, nicht unter dem
 * Zeiger. Reichte die Trefferfläche über die ganze Lücke, spränge er beim
 * Loslassen weit weg von der Hand. So bleibt der Sprung so klein wie die
 * Fläche, die man ohnehin nicht sieht.
 */
function hitRectOf(slot: SwapZone, zoom: number): MovingRect {
  const { rect } = slot
  const owner = slot.owner.rect
  const reach = EDGE_WIDTH / zoom
  const grow = (gap: number) => Math.min(gap, reach)
  if (rect.x + rect.width <= owner.x) return { ...rect, width: rect.width + grow(owner.x - (rect.x + rect.width)) }
  if (rect.x >= owner.x + owner.width) {
    const gap = grow(rect.x - (owner.x + owner.width))
    return { ...rect, x: rect.x - gap, width: rect.width + gap }
  }
  if (rect.y + rect.height <= owner.y) return { ...rect, height: rect.height + grow(owner.y - (rect.y + rect.height)) }
  if (rect.y >= owner.y + owner.height) {
    const gap = grow(rect.y - (owner.y + owner.height))
    return { ...rect, y: rect.y - gap, height: rect.height + gap }
  }
  return rect
}

/**
 * Welche Fläche der Zeiger trifft, und welche Ränder der getroffene Zettel hat.
 *
 * Erst die Zettel: Getroffen wird genau EINER — der, in dessen Kern oder Rand
 * der Zeiger steht. Deshalb geht ein Rand einem Feld vor, das bis an ihn
 * heranreicht (`hitRectOf`). Weil die Ränder höchstens in die halbe Lücke reichen,
 * überlappen sich die Flächen zweier Nachbarn nie. An einer Ecke, wo zwei
 * Ränder desselben Zettels sich überlagern, gewinnt die NÄHERE Kante — wie
 * bei den **Anlegepunkten**. Dann, im Freien, die **Leerplätze**: Unter
 * mehreren, die sich überlappen, gewinnt das Feld mit der näheren Mitte.
 * Kern und Rand gehen vor, wo sie in ein Feld hineinreichen.
 *
 * Gezeigt (als unsichtbare Marken) werden die Ränder des getroffenen Zettels
 * oder das getroffene Feld; der Kern nie. Was man sieht, ist das Ausweichen
 * der Zettel selbst — oder der Getragene, der ins Feld einrastet.
 */
export function swapZonesFor(
  entries: SwapEntry[],
  dragged: string,
  pointer: SwapPoint,
  zoom: number,
  options: SwapZonesOptions = {},
): SwapZones {
  const bands = options.bands ?? true
  const moved = entries.find(entry => entry.id === dragged)
  if (!moved) return { zones: [], hit: null }

  const reach = EDGE_WIDTH / zoom
  // Ein **geklebter** Zettel trägt weder **Kern** noch **Rand**: Er ist kein
  // Tauschpartner, und verdrängen lässt er sich auch nicht. Das verbietet NICHT
  // das Einreihen neben ihm — die **Fuge** zwischen ihm und seinem Nachbarn
  // hängt am **Rand des Nachbarn**, und weggeschoben wird dann der Nachbar, nie
  // er (`rowAfter` bricht an ihm ab). Was fehlt, ist allein der Griff, ihn
  // SELBST zu tauschen oder zu verdrängen. Siehe „Klebestreifen" in CONTEXT.md.
  const candidates = entries.filter(entry =>
    entry.id !== dragged
    && !entry.taped
    && pointer.x >= entry.rect.x - reach && pointer.x <= entry.rect.x + entry.rect.width + reach
    && pointer.y >= entry.rect.y - reach && pointer.y <= entry.rect.y + entry.rect.height + reach)

  let hit: SwapZone | null = null
  let shown: SwapZone[] = []
  let closest = Infinity
  for (const candidate of candidates) {
    const zones = zonesOf(candidate, entries, moved, zoom, bands)
    for (const zone of zones) {
      if (!contains(zone.rect, pointer)) continue
      // Der Kern zählt erst, wenn kein Rand trifft.
      const distance = zone.kind === 'on' ? Infinity : distanceToEdge(zone, pointer)
      if (hit === null || distance < closest) {
        hit = zone
        closest = distance
        shown = zones.filter(z => z.kind !== 'on')
      }
    }
  }
  if (hit) return { zones: shown, hit }

  if (!bands) return { zones: [], hit: null }
  const fields = freeSlotsFor(entries, dragged)
  let slot: SwapZone | null = null
  let abstand = Infinity
  for (const free of fields) {
    const zone: SwapZone = { owner: free.owner, kind: 'slot', rect: free.rect }
    if (!contains(hitRectOf(zone, zoom), pointer)) continue
    const distance = distanceToCentre(free.rect, pointer)
    if (slot === null || distance < abstand) {
      slot = zone
      abstand = distance
    }
  }
  /*
    **Der Rückfall**: In Reichweite der eigenen Gruppe gibt es keinen
    Fehlschlag (siehe `SwapZonesOptions.nearest`). Gemessen wird von der Mitte
    des GETRAGENEN, nicht vom Zeiger: Der Zettel landet im Feld, und der
    Zeiger sitzt am **Tauschpunkt** auf seiner oberen Kante — vom ihm aus
    gewänne bergab regelmäßig das falsche Feld.
  */
  const from = options.nearest
  if (slot === null && from) {
    const middle = { x: from.x + from.width / 2, y: from.y + from.height / 2 }
    for (const free of fields) {
      const distance = distanceToCentre(free.rect, middle)
      if (slot === null || distance < abstand) {
        slot = { owner: free.owner, kind: 'slot', rect: free.rect }
        abstand = distance
      }
    }
  }
  return { zones: slot ? [slot] : [], hit: slot }
}

/**
 * Ob ein einmal getroffener Bereich den Zeiger noch HÄLT — seine
 * Trefferfläche, nach allen Seiten um **eine Handbreit** gewachsen.
 *
 * Der Nachlass für die Schwelle zwischen **Andocken** und **Verschieben**
 * (CONTEXT.md, Tauschpunkt): Eine Fläche hat eine harte Kante, und eine Hand
 * am Knopf zittert. Ohne diesen Rand kippte der Zustand dort mehrmals pro
 * Sekunde — frei am Zeiger, eingerastet, frei —, und die Hilfslinien gingen
 * im selben Takt an und aus. Zurück ins Andocken geht es dagegen ohne
 * Nachlass: Dafür braucht es einen echten Treffer.
 *
 * Dasselbe Maß wie überall sonst hier (`EDGE_WIDTH`); eine zweite Zahl müsste
 * man erklären.
 */
export function holdsZone(zone: SwapZone, pointer: SwapPoint, zoom: number): boolean {
  const base = zone.kind === 'slot' ? hitRectOf(zone, zoom) : zone.rect
  const slack = EDGE_WIDTH / zoom
  return contains({
    x: base.x - slack,
    y: base.y - slack,
    width: base.width + 2 * slack,
    height: base.height + 2 * slack,
  }, pointer)
}

/**
 * Aus einem Treffer das Schiebe-Ziel — `null` beim Kern (Tausch), beim
 * Leerplatz und daheim (dort rückt niemand).
 */
export function shoveTargetOf(zone: SwapZone): ShoveTarget | null {
  if (zone.kind === 'on' || zone.kind === 'slot' || zone.kind === 'home' || !zone.axis) return null
  return { axis: zone.axis, anchor: zone.owner, side: zone.kind }
}
